import { Typography } from '@mui/material';
import {
  FilterKeys,
  FilterSection,
  GetStaffQueryFilters,
  STAFF_ARRANGE_BY_FILTER_KEYS,
  STAFF_QUERY_FILTER_KEYS,
  StaffArrangeBy,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  ArrangeByDropdown,
  DateDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  GenderExpandedSelect,
  GenderTagSelect,
  NationalityExpandedSelect,
  NationalityTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeExpandedSelect,
  PropertyTypeTagSelect,
  renderPropertyGroupTags,
  SubjectExpandedSelect,
  SubjectTagSelect,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { StaffFiltersSidebar } from './StaffFiltersSidebar';
import { useStaffFilters } from './useStaffFilters';

type StaffFiltersProps = {
  arrangeBy: StaffArrangeBy | null;
  onSetArrangeBy: (v: StaffArrangeBy | null) => void;
  schoolId: string;
  filters: GetStaffQueryFilters;
  defaultFilters: GetStaffQueryFilters;
  onSetFilters: (v: GetStaffQueryFilters) => void;
  defaultUserFilters: GetStaffQueryFilters;
  defaultUserArrangeBy: StaffArrangeBy | null;
};

export type StaffFilters = {
  openArrangeBy: () => void;
};

const StaffFiltersComponent = (
  {
    arrangeBy,
    defaultFilters,
    filters,
    schoolId,
    onSetFilters,
    onSetArrangeBy,
    defaultUserFilters,
    defaultUserArrangeBy,
  }: StaffFiltersProps,
  ref: React.ForwardedRef<StaffFilters>,
) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const {
    labels,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    filtersStatus,
    filtersHouse,
    filtersGender,
    filtersNationality,
    filtersDepartment,
    filtersSubject,
    onSetHouseId,
    onClearHouse,
    onSetGender,
    onClearGender,
    onSetNationality,
    onClearNationality,
    onSetStatusId,
    onClearStatus,
    getSchoolLevelById,
    getAgeGroupById,
    getAgeGroupsByLevelId,
    onSetDate,
    onSetAgeGroup,
    onClearAgeGroup,
    onSetDepartmentId,
    onClearDepartment,
    onSetSubjectId,
    onClearSubject,
  } = useStaffFilters({ schoolId, filters, onSetFilters });

  useImperativeHandle(
    ref,
    () => ({
      openArrangeBy: () => {
        setTimeout(() => arrangeByDropdown.current?.open(), 300);
      },
    }),
    [],
  );

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Staff}
        filters={{ ...filters, arrange_by: arrangeBy ?? undefined }}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(STAFF_QUERY_FILTER_KEYS, v);

          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });

          if (!v.arrange_by) {
            onSetArrangeBy(null);
            return;
          }
          for (const key of STAFF_ARRANGE_BY_FILTER_KEYS) {
            key === v.arrange_by && onSetArrangeBy(key);
          }
        }}
      />
      {filtersDate && <DateDropdown date={filtersDate} label={labels.date} onSetDate={onSetDate} />}

      {!!filtersStatus?.length && (
        <FilterDropdown
          onClear={onClearStatus}
          label={labels.status}
          tags={(open) =>
            filtersStatus.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Staff}
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.Status}
              userRole={SchoolUserRole.Staff}
              schoolId={schoolId}
              selectedIds={filtersStatus}
              onApply={onSetStatusId}
              onClose={onClose}
              onClear={onClearStatus}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          label={labels.age_group}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Staff,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Staff}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersHouse?.length && (
        <FilterDropdown
          onClear={onClearHouse}
          label={labels.house}
          tags={(open) =>
            filtersHouse.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Staff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.House}
              userRole={SchoolUserRole.Staff}
              schoolId={schoolId}
              selectedIds={filtersHouse}
              onApply={onSetHouseId}
              onClose={onClose}
              onClear={onClearHouse}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersNationality?.length && (
        <FilterDropdown
          onClear={onClearNationality}
          label={labels.nationality}
          tags={(open) =>
            filtersNationality.map((v) => (
              <NationalityTagSelect sx={{ maxWidth: 200 }} key={v} nationality={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <NationalityExpandedSelect
              selectedNationalities={filtersNationality}
              onApply={onSetNationality}
              onClose={onClose}
              onClear={onClearNationality}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersGender?.length && (
        <FilterDropdown
          onClear={onClearGender}
          label={labels.gender}
          tags={(open) =>
            filtersGender.map((v) => (
              <GenderTagSelect sx={{ maxWidth: 200 }} key={v} gender={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GenderExpandedSelect
              selectedGenders={filtersGender}
              onApply={onSetGender}
              onClose={onClose}
              onClear={onClearGender}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersSubject?.length && (
        <FilterDropdown
          onClear={onClearSubject}
          label={labels.subject}
          tags={(open) =>
            filtersSubject.map((id) => (
              <SubjectTagSelect
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <SubjectExpandedSelect
              schoolId={schoolId}
              selectedIds={filtersSubject}
              onApply={onSetSubjectId}
              onClose={onClose}
              onClear={onClearSubject}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersDepartment?.length && (
        <FilterDropdown
          onClear={onClearDepartment}
          label={labels.department}
          tags={(open) =>
            filtersDepartment.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Staff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.Department}
              userRole={SchoolUserRole.Staff}
              schoolId={schoolId}
              selectedIds={filtersDepartment}
              onApply={onSetDepartmentId}
              onClose={onClose}
              onClear={onClearDepartment}
            />
          )}
        </FilterDropdown>
      )}
      {arrangeBy && (
        <ArrangeByDropdown
          ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
          onSelectOption={onSetArrangeBy}
          selectedOption={arrangeBy}
          options={arrangeByDropdownOptions}
          onClear={() => onSetArrangeBy(null)}
          onRemove={() => onSetArrangeBy(null)}
        />
      )}
      <StaffFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={(v: { filters: GetStaffQueryFilters; arrangeBy: StaffArrangeBy | null }) => {
          onSetFilters(v.filters);
          onSetArrangeBy(v.arrangeBy);
        }}
        arrangeBy={arrangeBy}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StaffFilters = forwardRef(StaffFiltersComponent);
